// Live Boston weather for the home page. Open-Meteo is free, needs no key and
// allows requests straight from the browser, so the static site needs no backend.

const FORECAST_URL =
  "https://api.open-meteo.com/v1/forecast?latitude=42.3601&longitude=-71.0589" +
  "&current=temperature_2m,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,is_day" +
  "&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FNew_York";

const CACHE_KEY = "bostonWeather";
const CACHE_MS = 20 * 60 * 1000; // the forecast only updates every 15 minutes anyway

// WMO weather code -> [what the page draws, how heavy it is (0-1), how it reads]
const CODES = {
  0: ["clear", 0, "clear"],
  1: ["clear", 0, "mostly clear"],
  2: ["clouds", 0.45, "partly cloudy"],
  3: ["clouds", 1, "overcast"],
  45: ["fog", 0.8, "fog"],
  48: ["fog", 1, "freezing fog"],
  51: ["drizzle", 0.3, "light drizzle"],
  53: ["drizzle", 0.6, "drizzle"],
  55: ["drizzle", 1, "heavy drizzle"],
  56: ["drizzle", 0.4, "freezing drizzle"],
  57: ["drizzle", 0.8, "freezing drizzle"],
  61: ["rain", 0.3, "light rain"],
  63: ["rain", 0.6, "rain"],
  65: ["rain", 1, "heavy rain"],
  66: ["rain", 0.4, "freezing rain"],
  67: ["rain", 0.8, "freezing rain"],
  71: ["snow", 0.3, "light snow"],
  73: ["snow", 0.6, "snow"],
  75: ["snow", 1, "heavy snow"],
  77: ["snow", 0.2, "snow grains"],
  80: ["rain", 0.4, "rain showers"],
  81: ["rain", 0.7, "rain showers"],
  82: ["rain", 1, "heavy showers"],
  85: ["snow", 0.5, "snow showers"],
  86: ["snow", 0.9, "heavy snow showers"],
  95: ["storm", 0.8, "thunderstorms"],
  96: ["storm", 0.9, "thunderstorms with hail"],
  99: ["storm", 1, "thunderstorms with hail"],
};

// The weather a visitor can switch to from the home page. Drizzle and the
// nor'easter can only be reached with ?weather=, so the nor'easter stays a surprise.
export const PREVIEWS = [
  { kind: "clear", label: "Clear" },
  { kind: "clouds", label: "Clouds" },
  { kind: "fog", label: "Fog" },
  { kind: "rain", label: "Rain" },
  { kind: "snow", label: "Snow" },
  { kind: "storm", label: "Storm" },
];
export const KINDS = ["clear", "clouds", "fog", "drizzle", "rain", "snow", "storm", "noreaster"];

// How heavy each previewed weather is and how hard the wind blows (mph, + is to the right).
const PREVIEW_SCENES = {
  clear: { intensity: 0, windMph: 4 },
  clouds: { intensity: 0.7, windMph: 8 },
  fog: { intensity: 0.9, windMph: 3 },
  drizzle: { intensity: 0.6, windMph: 5 },
  rain: { intensity: 0.7, windMph: 10 },
  snow: { intensity: 0.7, windMph: 6 },
  storm: { intensity: 1, windMph: 18 },
  noreaster: { intensity: 1, windMph: -32 }, // nor'easters blow in from the northeast
};

// The saved weather if it is recent enough, read synchronously so the page can
// show it on its first paint.
export function readCachedWeather() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
    if (cached && Date.now() - cached.fetchedAt < CACHE_MS) return cached.data;
  } catch {
    // storage blocked or garbled: fetch again
  }
  return null;
}

// Resolves to the current Boston weather, or null if it could not be fetched.
export async function loadBostonWeather() {
  const cached = readCachedWeather();
  if (cached) return cached;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(FORECAST_URL, { signal: controller.signal });
    if (!res.ok) return null;
    const current = (await res.json()).current;
    if (!current || !(current.weather_code in CODES)) return null;
    const data = {
      tempF: Math.round(current.temperature_2m),
      code: current.weather_code,
      windSpeed: current.wind_speed_10m,
      windDir: current.wind_direction_10m,
      gusts: current.wind_gusts_10m,
      isDay: current.is_day === 1,
    };
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), data }));
    } catch {
      // not caching is fine
    }
    return data;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// Snow with a strong wind is a nor'easter.
function isNoreaster(kind, data) {
  return kind === "snow" && (data.windSpeed >= 20 || data.gusts >= 35);
}

// What the live weather looks like on the page, and the words for it.
export function describeLive(data) {
  const [kind, intensity, label] = CODES[data.code];
  // The wind's direction is where it blows from; only its left/right part shows on screen.
  const windMph = -Math.sin((data.windDir * Math.PI) / 180) * data.windSpeed;
  const noreaster = isNoreaster(kind, data);
  return {
    label,
    noreaster,
    scene: {
      kind: noreaster ? "noreaster" : kind,
      intensity: noreaster ? 1 : intensity,
      isDay: data.isDay,
      windMph,
    },
  };
}

function bostonIsDaytime() {
  const hour = Number(
    new Date().toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", hourCycle: "h23" })
  );
  return hour >= 7 && hour < 19;
}

// A previewed weather. It keeps Boston's real day or night unless `sky` says otherwise.
export function previewScene(kind, sky, live) {
  const isDay = sky ? sky === "day" : live ? live.isDay : bostonIsDaytime();
  return { kind, isDay, ...PREVIEW_SCENES[kind] };
}

// The wind right now, in mph: the steady wind with slow gusts on top. Everything
// that blows around (rain, snow, clouds, thrown icons) uses this, so they move together.
export function gustyWind(scene, seconds) {
  const gustiness = scene.kind === "noreaster" || scene.kind === "storm" ? 0.45 : 0.3;
  const gust = 1 + gustiness * Math.sin(seconds * 0.6) + 0.2 * Math.sin(seconds * 1.7 + 1.3);
  return scene.windMph * gust;
}
